import { Store } from '@sama/app-state';
import { pickExisting } from '../../../../shared/misc-utilities/misc-utils';
import { EventTrackingValue } from '../../../../team-apis/core/event-tracking';
import { Answer } from '../../../../team-apis/otterdam/answers/answer';
import {
  PointCloudOrthographicShapeType,
  PointCloudShapeType,
} from '../../../../team-apis/otterdam/answers/point-cloud-answer';
import {
  FrameSpecificTag,
  LocationSource,
  Point2D,
  Point3D,
  PointType,
  ShapeType,
  VectorTaggingShapeType,
} from '../../../../team-apis/otterdam/answers/shape-typings';
import { AnnotationState } from './annotation-state';
import { ShapeLocationState } from './shape-location-state';

export type ShapeState2D = ShapeState<Point2D, VectorTaggingShapeType> & {
  isProjection?: boolean;
};

export interface OrthographicShapeState3D extends ShapeState3D {
  type: PointCloudOrthographicShapeType;
}

export type ShapeState3D = ShapeState<Point3D, PointCloudShapeType>;

export const X = 0;
export const Y = 1;
export const Z = 2;

/**
 * When used for objectless projects: this class and those nested inside it
 * should use underscores for their key names, because it is used directly to
 * create the JSON delivered to the server.
 */
export interface ShapeState<
  P extends PointType = PointType,
  T extends ShapeType = ShapeType,
> extends AnnotationState {
  type: T;
  transparent?: boolean;
  showDimensions?: boolean;
  key_locations: Array<ShapeLocationState<P>>;
  source?: LocationSource;
  parentId?: number;
  outputName?: string;

  // linked shapes are shapes that should update each other when changed
  linkedShapeId?: number;
}

export interface KeypointShapeState extends ShapeState2D {
  keypointClass: string;
}

export function isKeypointShapeStore(
  store: Store<ShapeState>,
): store is Store<KeypointShapeState> {
  return 'keypointClass' in store.state();
}

export function getShapeForEventTracking(
  shape: ShapeState,
): EventTrackingValue {
  return pickExisting(
    shape,
    'index',
    'type',
    'source',
    'outputName',
    'parentId',
  );
}

export type StaticTags = StrictRecord<string, Answer>;
export type DynamicTags = StrictRecord<string, FrameSpecificTag[]>;
export type Tag = string | string[];

export interface TagsState {
  tags: StaticTags;
  dynamic_tags: DynamicTags;
}

export function isShapeStore(
  annotationStore: Store<AnnotationState>,
): annotationStore is Store<ShapeState> {
  return 'key_locations' in annotationStore.state();
}

export function isShapeState(
  annotationState: AnnotationState,
): annotationState is ShapeState {
  return 'key_locations' in annotationState;
}
